import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";

import { useAuthContext } from "contexts/AuthContext";
import { Spinner } from "stories/components";
import { clearSessionStorage } from "services/sessionStorage";
import { cancelLogoutWhenTokenExp } from "services/logoutTokenExp";
import {
  devLoginErrorMessage,
  readDevLoginToken,
  redeemDevLoginToken,
} from "services/devLogin";

/**
 * /dl?t=<token>: redeems an ERP one-time dev-login link (dev build only, see app.js).
 * On failure it stays here and tells why; it never redirects to /auth/login.
 */
const DevLogin = () => {
  const { login } = useAuthContext();
  const history = useHistory();
  const location = useLocation();
  const [error, setError] = useState(null);
  // The token is single-use: a second POST (re-render, StrictMode) would burn it.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return undefined;
    }
    started.current = true;
    let active = true;

    const token = readDevLoginToken(location.search);
    if (!token) {
      setError({ message: "Linkki ei kelpaa.", code: "missing_token" });
      return undefined;
    }

    redeemDevLoginToken(token).then(
      (response) => {
        const { access, refresh } = (response && response.data) || {};
        if (!access || !refresh) {
          if (active) {
            setError({ message: "Palvelin ei palauttanut istuntoa.", code: "bad_response" });
          }
          return;
        }
        cancelLogoutWhenTokenExp();
        clearSessionStorage();
        login(access, refresh);
        history.replace("/dashboard/frontpage");
      },
      (err) => {
        if (active) {
          setError(devLoginErrorMessage(err));
        }
      }
    );
    return () => {
      active = false;
    };
    // Mount only: the guard above makes later runs no-ops anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
      {error ? (
        <div className="text-center">
          <h2 className="mb-3">Dev-kirjautuminen epäonnistui</h2>
          <p data-testid="dev-login-error">
            {error.message} <code>({error.code})</code>
          </p>
          <Link to="/auth/login">Kirjautumissivulle</Link>
        </div>
      ) : (
        <Spinner animation="border" role="status" className="text-secondary" />
      )}
    </div>
  );
};

export default DevLogin;
